/**
 * Map a card's English name (as returned by the backend) to the filename
 * of its illustration in /public/cards/.
 *
 * Conventions (must match the file names dropped into public/cards/):
 *   Major Arcana:  "The Fool"        → "major-fool.jpeg"
 *                  "Wheel of Fortune" → "major-wheel-of-fortune.jpeg"
 *                  "The High Priestess" → "major-high-priestess.jpeg"
 *   Minor Arcana:  "Two of Wands"    → "minor-two-of-wands.jpeg"
 *                  "Ace of Pentacles"→ "minor-ace-of-pentacles.jpeg"
 *                  "Knight of Cups"  → "minor-knight-of-cups.jpeg"
 *
 * Returns the relative URL (served by the same origin as the SPA),
 * or `null` if the name can't be parsed — caller should fall back to
 * the procedural SVG `<TarotCard>` render in that case.
 */

const MAJOR_SLUG: Record<string, string> = {
  'The Fool': 'fool',
  'The Magician': 'magician',
  'The High Priestess': 'high-priestess',
  'The Empress': 'empress',
  'The Emperor': 'emperor',
  'The Hierophant': 'hierophant',
  'The Lovers': 'lovers',
  'The Chariot': 'chariot',
  'Strength': 'strength',
  'The Hermit': 'hermit',
  'Wheel of Fortune': 'wheel-of-fortune',
  'Justice': 'justice',
  'The Hanged Man': 'hanged-man',
  'Death': 'death',
  'Temperance': 'temperance',
  'The Devil': 'devil',
  'The Tower': 'tower',
  'The Star': 'star',
  'The Moon': 'moon',
  'The Sun': 'sun',
  'Judgement': 'judgement',
  'The World': 'world',
};

const MINOR_SUIT_SLUG: Record<string, string> = {
  Wands: 'wands',
  Cups: 'cups',
  Swords: 'swords',
  Pentacles: 'pentacles',
};

const MINOR_RANK_SLUG: Record<string, string> = {
  Ace: 'ace', Two: 'two', Three: 'three', Four: 'four', Five: 'five',
  Six: 'six', Seven: 'seven', Eight: 'eight', Nine: 'nine', Ten: 'ten',
  Page: 'page', Knight: 'knight', Queen: 'queen', King: 'king',
};

export function cardImagePath(name_en?: string | null): string | null {
  if (!name_en) return null;

  // Major
  const majorSlug = MAJOR_SLUG[name_en];
  if (majorSlug) return `/cards/major-${majorSlug}.jpeg`;

  // Minor — "{Rank} of {Suit}" → "minor-{rank}-of-{suit}.jpeg"
  const m = /^(\w+) of (Wands|Cups|Swords|Pentacles)$/.exec(name_en);
  if (m) {
    const rank = MINOR_RANK_SLUG[m[1]];
    const suit = MINOR_SUIT_SLUG[m[2]];
    if (rank && suit) return `/cards/minor-${rank}-of-${suit}.jpeg`;
  }

  return null;
}
